import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useCategoriesPartial from "../../data/useCategoriesPartial";
import usePartRequests from "../../data/usePartRequests";
import Modal from "../../layouts/Modal";
import { IPartCategory } from "../../models/Part";
import { PartRequestContactPreference } from "../../models/partRequests/IPartRequest";
import { PartRequestFormValues, toCreatePartRequest, validatePartRequestForm } from "../../utils/partRequests";
import useNotification from "../notifications/useNotifications";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { YearCombobox } from "../parts/YearCombobox";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

/** What the Stock page's filter form had on it when "Create part request" was pressed. */
export interface IPartRequestSearchSeed {
  vrm?: string;
  make?: string;
  model?: string;
  yearFrom?: string;
  yearTo?: string;
  engineCode?: string;
  categories: IPartCategory[];
}

interface CreatePartRequestFromSearchModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  seed: IPartRequestSearchSeed | null;
  /** The saved search this request came from, when the search was recorded. */
  stockSearchId: string | null;
}

const contactOptions = [
  { id: PartRequestContactPreference.Phone, name: "Phone" },
  { id: PartRequestContactPreference.Email, name: "Email" },
];

// The year the request carries is one year, not the window the search used. A window that is a
// single year is that year; otherwise the desk picks, because a generation window's first year is
// not the customer's car.
function seedYear(seed: IPartRequestSearchSeed | null): string {
  if (!seed?.yearFrom) return "";
  if (!seed.yearTo || seed.yearTo === seed.yearFrom) return seed.yearFrom;
  return "";
}

function toFormValues(seed: IPartRequestSearchSeed | null, stockSearchId: string | null): PartRequestFormValues {
  const category = seed?.categories?.[0];
  return {
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    contactPreference: PartRequestContactPreference.Phone,
    vrm: seed?.vrm ?? "",
    make: seed?.make ?? "",
    model: seed?.model ?? "",
    year: seedYear(seed),
    partWanted: category?.name ?? "",
    details: "",
    partCategoryId: category?.id ?? "",
    engineCode: seed?.engineCode ?? "",
    stockSearchId,
    partNumber: "",
    paintCode: "",
  };
}

/**
 * Raise an internal part request straight from the stock search: the car and the category come from
 * the form, so the desk only has to add who wants it. Lives in the Stock page rather than the Part
 * Requests page because that is where "we haven't got one" is discovered.
 */
export default function CreatePartRequestFromSearchModal({ open, setOpen, seed, stockSearchId }: CreatePartRequestFromSearchModalProps) {
  const { t } = useTranslation("stock");
  const { addNotification } = useNotification();
  const { create } = usePartRequests(undefined, "CreatePartRequestFromSearchModal");
  const { categories: allCategories } = useCategoriesPartial();
  const { register, handleSubmit, setValue, watch, reset } = useForm<PartRequestFormValues>({
    defaultValues: toFormValues(seed, stockSearchId),
  });

  // The seed changes every time the modal opens; the form follows it.
  useEffect(() => {
    if (open) reset(toFormValues(seed, stockSearchId));
  }, [open, seed, stockSearchId, reset]);

  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchYear = watch("year");
  const categoryOptions = allCategories.data?.data ?? [];
  const seededCategoryId = seed?.categories?.[0]?.id ?? "";

  const submit = (values: PartRequestFormValues) => {
    const problem = validatePartRequestForm(values);
    if (problem) {
      addNotification({ variant: "warn", primaryText: problem });
      return;
    }
    create.mutate(toCreatePartRequest(values), {
      onSuccess: () => setOpen(false),
    });
  };

  const vehicleSummary = [
    seed?.vrm?.trim().toUpperCase(),
    [seed?.make, seed?.model].filter(Boolean).join(" "),
    seed?.yearFrom && seed?.yearTo && seed.yearFrom !== seed.yearTo ? `${seed.yearFrom}–${seed.yearTo}` : seed?.yearFrom,
    seed?.engineCode,
  ].filter(Boolean).join(" · ");

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-3xl">
      <form onSubmit={handleSubmit(submit)} className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t("Create Part Request")}</h2>
          <p className="mt-1 text-sm text-gray-600">
            {t("Nothing in stock? Log what the caller wants so it is matched against vehicles coming in, or sent to the PartPro network.")}
          </p>
          {vehicleSummary && <p className="mt-2 text-sm font-medium text-gray-800">{t("Searched for")}: {vehicleSummary}</p>}
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("The part")}</h3>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {allCategories.isLoading ? null : (
              <ComboBoxInput
                key={`${open}-${seededCategoryId}`}
                label={t("Category")}
                options={categoryOptions}
                searchField="name"
                valueField="id"
                register={register}
                setValue={setValue}
                registerName="partCategoryId"
                initialValue={seededCategoryId}
                placeholder={t("Search categories...")}
              />
            )}
            <TextInput label={t("Part wanted")} register={register} registerName="partWanted" required placeholder={t("e.g. Driver side front door, alternator, N/S rear light")} />
            <TextInput label={t("Details")} register={register} registerName="details" richText rows={2} cols="sm:col-span-2" placeholder={t("Colour, trim, anything that narrows it down")} />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("The vehicle")}</h3>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-5">
            <TextInput label={t("Registration")} register={register} registerName="vrm" placeholder="AB12 CDE" customClasses="uppercase" />
            <MakeCombobox
              label={t("Make")}
              currentValue={watchMake}
              onMakeSelect={(selected) => {
                setValue("make", selected?.make ?? "");
                setValue("model", "");
              }}
            />
            <ModelCombobox
              label={t("Model")}
              make={watchMake}
              currentValue={watchModel}
              onModelSelect={(selected) => setValue("model", selected?.range ?? "")}
            />
            <YearCombobox
              label={t("Year")}
              currentValue={watchYear}
              placeholder={t("Select year...")}
              onYearSelect={(selected) => setValue("year", selected ?? "")}
            />
            <TextInput label={t("Engine Code")} register={register} registerName="engineCode" customClasses="uppercase" />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("The customer")}</h3>
          <p className="text-xs text-gray-500">{t("Kept in Wilma only - never shared if the request goes out to the network.")}</p>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextInput label={t("Name")} register={register} registerName="customerName" required />
            <SelectInput label={t("Prefers")} register={register} registerName="contactPreference" options={contactOptions} setValue={setValue} watch={watch} />
            <TextInput label={t("Phone")} register={register} registerName="customerPhone" type="tel" />
            <TextInput label={t("Email")} register={register} registerName="customerEmail" type="email" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <ButtonInput label={t("Cancel")} classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300" onClick={() => setOpen(false)} />
          <ButtonInput label={t("Log request")} isSubmit onClick={undefined} classes="" isLoading={create.isLoading} />
        </div>
      </form>
    </Modal>
  );
}
